import { IUseFetchOptionsParams } from '@common/api.models';
import { isValidApiBaseProps } from '@common/api.services';
import { FETCH_QUERIES_NAME } from '@common/constants';
import { createUseFetchOptionsCustomPropsFn, createUseFetchOptionsNoPropsFn } from '@common/selectOptions.services';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { useQuery } from '@tanstack/react-query';

import { AxiosErrorResponse } from '@core/error.models';
import {
  getGlobalProjectRolesWithUserAsync,
  getGlobalRoleOptionsAsync,
  getProjectRoleOptionsAsync,
  getProjectRolesWithUserAsync,
  getRoleDistributionOptionsAsync,
  getRolesProjectRequestAsync,
  IGetProjectRequestRoleOptionsProps,
  IGetProjectRoleOptionsProps,
  IGetProjectRolesWithUserProps,
} from './projectRole.services';
import { IProjectRole } from './projectRole.models';

export const useGetProjectRolesWithUser = (props: IGetProjectRolesWithUserProps, enabled = true) => {
  return useQuery<IProjectRole[], AxiosErrorResponse>(
    [props.projectId, props.companyId, FETCH_QUERIES_NAME.ROLES_WITH_USER],
    async () => {
      const result = await getProjectRolesWithUserAsync(props);

      return result.items || [];
    },
    {
      enabled: isValidApiBaseProps(props) && isPositiveNumber(props.companyId) && enabled,
    },
  );
};

export const useGetGlobalProjectRolesWithUserAsync = (enabled = true) => {
  return useQuery<IProjectRole[], AxiosErrorResponse>(
    [FETCH_QUERIES_NAME.GLOBAL_ROLES_WITH_USER],
    async () => {
      const result = await getGlobalProjectRolesWithUserAsync();

      return result?.items || [];
    },
    {
      enabled,
    },
  );
};

export const useFetchGlobalRoleOptions = ({ isDirty = false, shouldConvertOptions = true }: IUseFetchOptionsParams) =>
  createUseFetchOptionsNoPropsFn({
    queryName: FETCH_QUERIES_NAME.GLOBAL_ROLE_OPTIONS,
    getOptionsFn: getGlobalRoleOptionsAsync,
    handleOptionsProps: {
      shouldConvertOptions,
      textFieldName: 'name',
    },
    enabled: !isDirty,
  });

export const useGetRoleOptionsForProjectOrProjectRequest = (
  props: IGetProjectRequestRoleOptionsProps,
  enabled = true,
) => {
  if (props.projectId && props.companyTypeId && props.organizationTypeId) {
    return useGetProjectRoleOptions(
      {
        projectId: props.projectId,
        companyTypeId: props.companyTypeId,
        organizationTypeId: props.organizationTypeId,
        shouldIncludeHidden: props.shouldIncludeHidden,
      },
      enabled,
    );
  }
  return useGetRolesProjectRequest(props, enabled);
};

export const useFetchProjectRoleOptions = ({ isDirty = false, shouldConvertOptions = true }: IUseFetchOptionsParams) =>
  createUseFetchOptionsCustomPropsFn({
    queryName: FETCH_QUERIES_NAME.ROLE_OPTIONS,
    getOptionsFn: getProjectRoleOptionsAsync,
    handleOptionsProps: { shouldConvertOptions, textFieldName: 'name' },
    enabled: !isDirty,
  });

export const useGetProjectRoleOptions = (props: IGetProjectRoleOptionsProps, enabled = true) => {
  return useQuery(
    [props.projectId, props.companyTypeId, FETCH_QUERIES_NAME.ROLE_OPTIONS],
    async () => {
      const result = await getProjectRoleOptionsAsync(props);

      return result.items || [];
    },
    {
      enabled:
        isValidApiBaseProps(props) &&
        isPositiveNumber(props.companyTypeId) &&
        isPositiveNumber(props.organizationTypeId) &&
        enabled,
    },
  );
};
export const useGetRolesProjectRequest = (props: IGetProjectRequestRoleOptionsProps, enabled = true) => {
  return useQuery(
    [props.companyTypeId, props.organizationTypeId, FETCH_QUERIES_NAME.ROLES],
    async () => {
      const result = await getRolesProjectRequestAsync(props);
      return result.items || [];
    },
    {
      enabled,
    },
  );
};

export const useFetchRoleDistributionOptions = ({
  isDirty = false,
  shouldConvertOptions = true,
}: IUseFetchOptionsParams) =>
  createUseFetchOptionsCustomPropsFn({
    queryName: FETCH_QUERIES_NAME.ROLE_DISTRIBUTION_OPTIONS,
    getOptionsFn: getRoleDistributionOptionsAsync,
    handleOptionsProps: { shouldConvertOptions, textFieldName: 'name' },
    enabled: !isDirty,
  });
